import { useRef } from 'react';

import { useConfigContext } from 'lib/core/config';

import SoftphoneModal from 'lib/common/contexts/SoftphoneModalContext/components/SoftphoneModal';

import Context, { TReplaceTextCallback } from './Context';
import { IQResponseState } from './components/constants/IQResponseState';
import { useIQActionMenu } from './hooks/useIQActionMenu';
import { useIQApi } from './hooks/useIQApi';
import { useIQModal } from './hooks/useIQModal';
import { getIQContextBaseState } from './utils/getIQContextBaseState';

/*
 * Rendered within the softphone modal context to be able to slide out the side bar
 * may change in the future if needed
 * */
function AIContextProvider({ children }) {
  const { config } = useConfigContext();
  const { IQEnabled, IQFeaturesEnabled, IQFeatureConfig } = getIQContextBaseState(config);
  const replaceTextCallbackRef = useRef<TReplaceTextCallback>();

  const {
    actions: { callIQServiceAction, reCallLastIQServiceAction, abortActiveRequest, resetIQApiState },
    state: { requestState, response, streamingState },
  } = useIQApi();

  const {
    actions: { openIQModal, closeIQModal, handleClearIQModalContent },
    state: { isIQModalOpen, iqModalOptions },
  } = useIQModal();

  const {
    actions: { openActionMenu, closeActionMenu, selectAction, setIQInput, selectMode, resetIQActionMenuState },
    state: { isActionMenuOpen, selectedAction, iqInput, selectedMode },
  } = useIQActionMenu({ resetIQApiState });

  const handleCloseIQModal = () => {
    closeIQModal();
    abortActiveRequest();

    setTimeout(() => {
      resetIQActionMenuState();
    }, 300);
  };

  return (
    <Context.Provider
      value={{
        state: {
          IQEnabled,
          IQFeaturesEnabled,
          IQFeatureConfig,
          isActionMenuOpen,
          selectedAction,
          iqInput,
          selectedMode,
          isIQModalOpen,
          replaceTextCallbackRef,
          requestState,
          isStreaming: requestState === IQResponseState.THINKING || requestState === IQResponseState.RETHINKING,
          response,
          streamingState,
        },
        actions: {
          openActionMenu,
          closeActionMenu,
          selectAction,
          setIQInput,
          selectMode,
          resetIQActionMenuState,
          openIQModal,
          closeIQModal: handleCloseIQModal,
          resetIQApiState,
          abortActiveRequest,
          think: callIQServiceAction,
          rethink: () => reCallLastIQServiceAction(true),
          retryFailedRequest: () => reCallLastIQServiceAction(false),
        },
      }}
    >
      {children}
      {iqModalOptions && (
        <SoftphoneModal
          isModalOpen={isIQModalOpen}
          closeModal={closeIQModal}
          handleClearContent={handleClearIQModalContent}
          isReversedLayout={false}
          {...iqModalOptions}
        >
          {iqModalOptions.content}
        </SoftphoneModal>
      )}
    </Context.Provider>
  );
}

export default AIContextProvider;
